import type { TrackedTranslation, TranslationTag } from '@ps/web-core-intl';
import { useTrackers } from '@ps/web-shared-analytics';
import { StarsButton, Translation, useTrackingLabel } from '@ps/web-shared-components';
import classNames from 'clsx';
import type { FormApi } from 'final-form';
import { uniqBy } from 'lodash';
import type { NamedExoticComponent, PropsWithChildren } from 'react';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { useIntl } from 'react-intl';

import useOnClickOutside from '@swf/core/hooks/useOnClickOutside';

import { TXTCLI_Clear } from './MultipleSelect.messages.json';
import styles from './MultipleSelect.scss';
import MultipleSelectCustomRange from './MultipleSelectCustomRange/MultipleSelectCustomRange';
import MultipleSelectGroupToggle from './MultipleSelectGroupToggle/MultipleSelectGroupToggle';
import type { OptionToggle, TOnChange } from './MultipleSelectToggle/MultipleSelectToggle';
import MultipleSelectToggle from './MultipleSelectToggle/MultipleSelectToggle';

interface ChildrenProps {
  onChange?: TOnChange;
  onToggleChange?: (checked: boolean) => void;
}

interface StyleProps {
  classes?: {
    container: string;
    title: string;
    titleActive: string;
    option: string;
    optionWrapper: string;
    dropdownOpen: string;
    dropdownActive: string;
    optionsWrapper?: string;
    optionsWrapperOpen?: string;
    optionsList?: string;
  };
}

interface MultipleSelectProps extends StyleProps {
  id: string;
  title: TranslationTag;
  defaultOption: OptionToggle;
  onSelect: (id: string, selectedOptions: OptionToggle[]) => void;
  dataTestId?: string;
  trackingLabel: TrackedTranslation;
  showNumberLabel?: boolean;
  numberLabelThreshold?: number;
}

interface MultipleSelectCompound {
  CustomRange: typeof MultipleSelectCustomRange;
  Toggle: typeof MultipleSelectToggle;
  GroupToggle: typeof MultipleSelectGroupToggle;
}

const DEFAULT_NUMBER_THRESHOLD = 3;

const MultipleSelect: React.FC<React.PropsWithChildren<MultipleSelectProps>> = ({
  id,
  title,
  classes,
  onSelect,
  dataTestId,
  defaultOption,
  trackingLabel,
  showNumberLabel,
  numberLabelThreshold = DEFAULT_NUMBER_THRESHOLD,
  children
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<OptionToggle[]>([]);
  const { inputFocused } = useTrackers();
  const fieldName = useTrackingLabel(trackingLabel);
  const { formatMessage } = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);

  const childrenList = React.Children.toArray(children);
  const childrenIdList = childrenList.map((el) => React.isValidElement(el) && el.props?.id);
  const hasSelectedOption = selectedOptions.length > 0;

  const optionClasses = useMemo(
    () => ({
      toggle: classes?.option,
      wrapper: classes?.optionWrapper
    }),
    [classes]
  );
  const dropdownLabel = useMemo(() => {
    let label: React.ReactNode = '';
    if (hasSelectedOption) {
      const optionsLength = selectedOptions.length;
      if (showNumberLabel && optionsLength > numberLabelThreshold) label += `[ ${optionsLength} ] `;
      label += selectedOptions.map((option) => formatMessage(option.label)).join(', ');
    } else {
      label = <Translation {...defaultOption.label} />;
    }
    return label;
  }, [
    selectedOptions,
    hasSelectedOption,
    defaultOption.label,
    showNumberLabel,
    numberLabelThreshold,
    formatMessage
  ]);

  useOnClickOutside(containerRef, () => setIsOpen(false));

  const handleOnClear = useCallback(
    (form: FormApi) => {
      form.reset();
      setSelectedOptions([]);
      onSelect(id, []);
    },
    [id, onSelect]
  );

  const handleOptionsChanged = useCallback(
    (options: OptionToggle[], checked: boolean, form: FormApi) => {
      const { values } = form.getState();
      if (childrenIdList.every((id) => values[id])) return handleOnClear(form);

      setSelectedOptions((prevState) => {
        let selectedItems = prevState;

        options.forEach(
          (option) =>
            (selectedItems = !checked
              ? selectedItems.filter((selected) => selected.value !== option.value)
              : [...selectedItems, option])
        );

        selectedItems = uniqBy(selectedItems, 'id');

        return selectedItems;
      });
    },
    [childrenIdList, handleOnClear]
  );

  useEffect(() => {
    onSelect(id, selectedOptions);
  }, [onSelect, id, selectedOptions]);

  const handleDropDownClicked = (e: React.MouseEvent) => {
    if (e.target instanceof HTMLDivElement || e.target instanceof HTMLSpanElement)
      setIsOpen((prev) => !prev);
  };

  const handleDropDownFocused = () => inputFocused({ fieldName, fieldType: 'dropdown' });

  const handleCustomRangeToggleChanged = useCallback((checked: boolean) => {
    if (checked) setSelectedOptions([]);
  }, []);

  if (childrenList.length === 0) return null;

  return (
    <div
      className={classNames(styles.container, classes?.container)}
      ref={containerRef}
      data-testid={dataTestId}
    >
      <Form
        onSubmit={() => undefined}
        render={({ form, values: { [id]: defaultValue } }) => (
          <>
            <p
              className={classNames(styles.title, classes?.title, {
                [styles.titleActive]: defaultValue || hasSelectedOption,
                [classes?.titleActive || '']: defaultValue || hasSelectedOption
              })}
            >
              <Translation {...title} />
            </p>
            <div
              className={classNames(styles.dropdown, {
                [styles.dropdownOpen]: isOpen,
                [classes?.dropdownOpen || '']: isOpen
              })}
              onClick={handleDropDownClicked}
              onFocus={handleDropDownFocused}
              tabIndex={0}
            >
              {isOpen ? (
                <MultipleSelectToggle
                  id={defaultOption.id}
                  value={defaultOption.value}
                  label={defaultOption.label}
                  onChange={() => handleOnClear(form)}
                  checked={!defaultValue && selectedOptions.length === 0}
                  classes={optionClasses}
                />
              ) : (
                <span
                  className={classNames(styles.dropdownLabel, {
                    [classes?.dropdownActive || '']: defaultValue || hasSelectedOption
                  })}
                >
                  {defaultValue || dropdownLabel}
                </span>
              )}
            </div>
            <div
              className={classNames(styles.optionsWrapper, classes?.optionsWrapper, {
                [styles.optionsWrapperOpen]: isOpen,
                [classes?.optionsWrapperOpen || '']: isOpen
              })}
            >
              <ul className={classNames(styles.optionsList, classes?.optionsList)}>
                {childrenList.map((child) => {
                  if (React.isValidElement(child)) {
                    let props: ChildrenProps = {};
                    switch (child.type) {
                      case MultipleSelectCustomRange:
                        props = { onToggleChange: handleCustomRangeToggleChanged };
                        break;
                      case MultipleSelectGroupToggle:
                      case MultipleSelectToggle:
                      default:
                        props = {
                          onChange: (options, checked) =>
                            handleOptionsChanged(options, checked, form)
                        };
                    }
                    return React.cloneElement(child, props);
                  }
                  return null;
                })}
              </ul>
              <StarsButton
                translation={TXTCLI_Clear}
                variant="text"
                onClick={() => handleOnClear(form)}
                className={styles.clearButton}
              />
            </div>
          </>
        )}
      />
    </div>
  );
};

const MultipleSelectMemoized = memo(MultipleSelect) as NamedExoticComponent<
  PropsWithChildren<MultipleSelectProps>
> &
  MultipleSelectCompound;

MultipleSelectMemoized.CustomRange = MultipleSelectCustomRange;
MultipleSelectMemoized.Toggle = MultipleSelectToggle;
MultipleSelectMemoized.GroupToggle = MultipleSelectGroupToggle;

export default MultipleSelectMemoized;
